// Email code step of the website password login (#666).
//
// The password is checked on a separate, non-persistent Firebase app, so the
// real session does not exist yet. That password session starts a code
// (websiteLoginOtpBegin); the correct code is exchanged for a custom token
// (websiteLoginOtpVerify) and only then is the site's own session signed in.
// Cancelling, or any failure before that, leaves nobody signed in.
//
// The UI-only step: the backend does not yet refuse a session that skipped
// the code (Rules enforcement is a follow-up).
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { firebaseApp } from "@/firebase/init.js";

export const LOGIN_CODE_LENGTH = 6;
const APP_NAME = "website-login-code";
const REGION = "europe-west1";

const GENERIC = "Si è verificato un errore. Riprova.";

/** After a resend, or after a dead attempt was replaced by a new one (#861). */
export const LOGIN_CODE_RESENT_MESSAGE = "Ti abbiamo inviato un nuovo codice. Controlla la tua casella email.";
/** The server's send limits (per challenge, account, IP): never worked around. */
export const LOGIN_CODE_LIMITED_MESSAGE = "Hai richiesto troppi codici, riprova tra qualche minuto.";
/**
 * The code was checked against an attempt that no longer exists: a newer login
 * (another tab, newarc.it's login iframe, another device) retired it, or it
 * was already used. Not a wrong code: "Invia di nuovo" starts a new one (#861).
 */
export const LOGIN_CODE_STALE_MESSAGE =
  "Questo codice non è più valido. Premi «Invia di nuovo» per riceverne uno nuovo.";
export const LOGIN_CODE_EXPIRED_MESSAGE =
  "Il codice è scaduto. Premi «Invia di nuovo» per riceverne uno nuovo.";

// Server reasons (details.reason) that end the attempt: the user has to start
// again from email and password.
const FINAL_REASONS = [
  "too_many_attempts",
  "expired",
  "reauth_required",
  "not_eligible",
  "email_unverified",
  // The password session behind the attempt is gone: retrying cannot help.
  "unauthenticated",
  "invalid_request",
];

// Resend refusals that mean "this attempt is dead", not "slow down" (#861).
// The server keeps one live website challenge per user: a newer begin (another
// tab, the newarc.it iframe, another device) or a used code retires it, and
// from then on resend answers invalid_code. The same password session cannot
// begin again after 5 minutes, so the attempt is restarted from the password.
// Never rate_limited or too_many_attempts: restarting would bypass the limits.
const RESTART_REASONS = ["invalid_code", "expired", "unauthenticated", "reauth_required"];

/**
 * invalid_code WITHOUT attemptsRemaining: the server checked the code against
 * an unknown, retired or used challenge. A wrong code on a live challenge
 * always carries attemptsRemaining (or ends in too_many_attempts).
 */
function isStaleCode(error) {
  const details = (error && error.details) || {};
  return loginCodeReason(error) === "invalid_code" && typeof details.attemptsRemaining !== "number";
}

export function loginCodeReason(error) {
  const reason = error && error.details && error.details.reason;
  if (typeof reason === "string" && reason) return reason;
  // A callable that is missing or unreachable surfaces as a bare `internal`.
  const code = String((error && error.code) || "");
  if (code.endsWith("internal") || code.endsWith("unavailable") || code.endsWith("not-found")) {
    return "unavailable";
  }
  return "unknown";
}

// #888: a platform account (staff, agency, professional) never signs in to the
// site. The server names its platform in `details.brand`.
const PLATFORM_NAMES = { newarc: "Newarc", immaginacasa: "Immaginacasa" };

export function platformAccountMessage(brand) {
  const name = PLATFORM_NAMES[brand];
  return `Questo è un account della piattaforma${name ? ` ${name}` : ""}: accedi dalla piattaforma, non da questo sito.`;
}

export function loginCodeMessage(error) {
  const details = (error && error.details) || {};
  switch (loginCodeReason(error)) {
    case "invalid_code":
      // Only a wrong code on a live attempt is "non valido" (#861).
      return typeof details.attemptsRemaining === "number"
        ? `Codice non valido. Tentativi rimasti: ${details.attemptsRemaining}.`
        : LOGIN_CODE_STALE_MESSAGE;
    case "expired":
      return LOGIN_CODE_EXPIRED_MESSAGE;
    case "too_many_attempts":
      return "Troppi tentativi. Accedi di nuovo più tardi.";
    case "rate_limited":
      return LOGIN_CODE_LIMITED_MESSAGE;
    case "delivery_failed":
      return "Non è stato possibile inviare il codice. Riprova.";
    case "email_unverified":
      return "Verifica prima la tua email: controlla la tua casella di posta.";
    case "not_eligible":
      return "Accesso non consentito per questo account.";
    case "platform_account":
      return platformAccountMessage(details.brand);
    case "reauth_required":
    case "unauthenticated":
      return "Accedi di nuovo con email e password.";
    case "invalid_request":
      return "Richiesta non valida. Accedi di nuovo.";
    case "unavailable":
      return "Il servizio di verifica non è raggiungibile. Riprova.";
    default:
      return GENERIC;
  }
}

// One message for every credential failure of the password step: an unknown
// account and a wrong password must read the same, or the form tells anyone
// which emails are registered (email-enumeration protection is off in both
// Firebase projects).
export const LOGIN_CREDENTIALS_MESSAGE = "Email o password non corretti.";

const CREDENTIAL_CODES = [
  "user-not-found",
  "wrong-password",
  "invalid-credential",
  "invalid-login-credentials",
  "invalid-email",
  "user-disabled",
];

/**
 * The message for an error thrown by the password step (flow.start). Never
 * the raw Firebase text: it names the failing code.
 */
export function passwordLoginMessage(error) {
  const code = String((error && error.code) || "").replace(/^auth\//, "");
  if (CREDENTIAL_CODES.includes(code)) return LOGIN_CREDENTIALS_MESSAGE;
  if (code === "too-many-requests") return "Troppe richieste. Riprova tra qualche minuto.";
  if (code === "network-request-failed") return "Connessione non disponibile. Controlla la rete e riprova.";
  return GENERIC;
}

/** 32 base64url characters (192 bits) identifying this browser attempt. */
export function newLoginTxn(getRandomValues = (a) => crypto.getRandomValues(a)) {
  const bytes = getRandomValues(new Uint8Array(24));
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * The login flow over injected ports (tested without Firebase):
 * - passwordSignIn(email, password) -> user   (throws Firebase auth errors)
 * - call(name, data) -> data                  (callable on the password app)
 * - signInWithCustomToken(token)              (the site's real session)
 * - endPasswordSession()
 */
export function createLoginCodeFlow(ports, newTxn = newLoginTxn) {
  let attempt = null; // { txn, challengeId, brand }
  // Kept only while the code step is open, to restart an attempt the server
  // has retired (#861); forgotten with the attempt.
  let credentials = null; // { email, password }
  // Bumped by every end(): a restart still in flight after Annulla, or after
  // a new start, must not bring its attempt back.
  let generation = 0;

  async function end() {
    attempt = null;
    credentials = null;
    generation += 1;
    try {
      await ports.endPasswordSession();
    } catch (e) {
      // Nothing to clean up.
    }
  }

  const reauth = () => ({ ok: false, final: true, message: loginCodeMessage({ details: { reason: "reauth_required" } }) });

  function limited(error) {
    const retry = error && error.details && error.details.retryAfterSec;
    return {
      ok: false,
      final: false,
      message: LOGIN_CODE_LIMITED_MESSAGE,
      resendInSec: typeof retry === "number" ? retry : undefined,
    };
  }

  /**
   * A new challenge for the same account and brand, from the password: the
   * old one is dead and its password session may be too old to begin again
   * (the server wants a sign-in from the last 5 minutes). The server's send
   * limits apply to this begin as to any other.
   */
  async function restart() {
    if (!attempt || !credentials) return reauth();
    const gen = generation;
    const { brand } = attempt;
    let user;
    try {
      user = await ports.passwordSignIn(credentials.email, credentials.password);
    } catch (e) {
      if (gen !== generation) return reauth();
      const code = String((e && e.code) || "").replace(/^auth\//, "");
      // The password no longer works (changed, account disabled): start over.
      if (CREDENTIAL_CODES.includes(code)) {
        await end();
        return { ok: false, final: true, message: passwordLoginMessage(e) };
      }
      return { ok: false, final: false, message: passwordLoginMessage(e) };
    }
    if (gen !== generation) return reauth();
    if (!user || !user.emailVerified) {
      await end();
      return { ok: false, final: true, message: loginCodeMessage({ details: { reason: "email_unverified" } }) };
    }
    const txn = newTxn();
    try {
      const r = await ports.call("websiteLoginOtpBegin", { v: 1, txn, brand });
      if (gen !== generation) return reauth();
      attempt = { txn, challengeId: r.challengeId, brand };
      return { ok: true, final: false, restarted: true, message: LOGIN_CODE_RESENT_MESSAGE, resendInSec: r.resendInSec };
    } catch (e) {
      if (gen !== generation) return reauth();
      const reason = loginCodeReason(e);
      if (reason === "rate_limited") return limited(e);
      const final = FINAL_REASONS.includes(reason);
      if (final) await end();
      return { ok: false, final, message: loginCodeMessage(e) };
    }
  }

  return {
    /**
     * Checks the password and mails the code. Wrong credentials throw the
     * Firebase auth error, as signInWithEmailAndPassword did; the forms map it
     * with passwordLoginMessage. Otherwise resolves to
     * { status: "code", maskedEmail, resendInSec } | { status: "unverified" }
     * | { status: "error", message }.
     */
    async start(email, password, brand) {
      await end();
      const user = await ports.passwordSignIn(email, password);
      if (!user || !user.emailVerified) {
        await end();
        return { status: "unverified" };
      }
      const txn = newTxn();
      try {
        const r = await ports.call("websiteLoginOtpBegin", { v: 1, txn, brand });
        attempt = { txn, challengeId: r.challengeId, brand };
        credentials = { email, password };
        return { status: "code", maskedEmail: r.maskedEmail, resendInSec: r.resendInSec };
      } catch (e) {
        await end();
        return { status: "error", message: loginCodeMessage(e), reason: loginCodeReason(e) };
      }
    },

    /**
     * { ok, message, resendInSec, final, restarted? }. A refusal that means
     * the attempt is dead (retired by a newer login, used, expired, password
     * session gone) starts a new attempt instead of showing an error (#861).
     */
    async resend() {
      if (!attempt) return reauth();
      try {
        const r = await ports.call("websiteLoginOtpResend", {
          v: 1,
          txn: attempt.txn,
          challengeId: attempt.challengeId,
          brand: attempt.brand,
        });
        return { ok: true, message: LOGIN_CODE_RESENT_MESSAGE, resendInSec: r.resendInSec, final: false };
      } catch (e) {
        const reason = loginCodeReason(e);
        if (RESTART_REASONS.includes(reason)) return restart();
        if (reason === "rate_limited") return limited(e);
        return {
          ok: false,
          message: loginCodeMessage(e),
          final: FINAL_REASONS.includes(reason),
        };
      }
    },

    /**
     * { ok, message, final, stale? }. On success the site session is signed
     * in. `stale`: the attempt behind the code is retired or expired, so no
     * code can work until "Invia di nuovo" starts a new one.
     */
    async verify(code) {
      const digits = String(code || "").replace(/\D+/g, "");
      if (digits.length !== LOGIN_CODE_LENGTH) {
        return { ok: false, final: false, message: "Inserisci il codice di 6 cifre." };
      }
      if (!attempt) return reauth();
      let token;
      try {
        const r = await ports.call("websiteLoginOtpVerify", {
          v: 1,
          txn: attempt.txn,
          challengeId: attempt.challengeId,
          code: digits,
        });
        token = r.customToken;
      } catch (e) {
        const reason = loginCodeReason(e);
        if (credentials && (isStaleCode(e) || reason === "expired")) {
          return { ok: false, final: false, stale: true, message: loginCodeMessage(e) };
        }
        const final = FINAL_REASONS.includes(reason);
        // Nothing can follow a final refusal: drop the password session now
        // instead of waiting for the user to press Annulla.
        if (final) await end();
        return { ok: false, message: loginCodeMessage(e), final };
      }
      try {
        await ports.signInWithCustomToken(token);
      } catch (e) {
        await end();
        return { ok: false, final: true, message: GENERIC };
      }
      await end();
      return { ok: true, final: false, message: "" };
    },

    cancel: end,
  };
}

/** Firebase-backed ports: a second app without persistence for the password. */
export function firebaseLoginCodePorts() {
  const app =
    firebase.apps.find((a) => a.name === APP_NAME) || firebase.initializeApp(firebaseApp.options, APP_NAME);
  const auth = app.auth();
  const functions = getFunctions(app, REGION);
  if (process.env.VUE_APP_USE_EMULATOR === "true") {
    auth.useEmulator(`http://localhost:${process.env.VUE_APP_EMULATOR_AUTH_PORT || 9099}`, { disableWarnings: true });
    connectFunctionsEmulator(functions, "localhost", Number(process.env.VUE_APP_EMULATOR_FUNCTIONS_PORT || 5001));
  }
  const ready = auth.setPersistence(firebase.auth.Auth.Persistence.NONE);
  return {
    async passwordSignIn(email, password) {
      await ready;
      const credential = await auth.signInWithEmailAndPassword(email, password);
      return credential.user;
    },
    async call(name, data) {
      const result = await httpsCallable(functions, name)(data);
      return result.data;
    },
    async signInWithCustomToken(token) {
      await firebase.auth().signInWithCustomToken(token);
    },
    async endPasswordSession() {
      await auth.signOut();
    },
  };
}

let sharedPorts = null;

/**
 * A fresh flow for each login attempt, over the one set of ports (the password
 * app is shared). A single flow would let a login started in one component
 * cancel a code step in flight in another — the navbar, the login iframe and
 * the videocall popup can all be mounted at once.
 */
export function websiteLoginCode() {
  if (!sharedPorts) sharedPorts = firebaseLoginCodePorts();
  return createLoginCodeFlow(sharedPorts);
}
